import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext, TasksContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import TripReviewCard from '@/components/TripReviewCard';
import { Bot, X, Send, Loader2, ChevronDown, Sparkles, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Am I overloaded today?",
  "Can I fit tennis tonight?",
  "What have you learned about my routines?",
  "What should I focus on now?",
  "How's my schedule looking this week?",
  "Help me protect some breathing space",
  "What tasks should I reschedule?",
];

const ContextBadge = ({ context }) => {
  if (!context) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-white/3 text-xs">
      <span className="flex items-center gap-1 text-teal-400">
        <Clock size={11} /> {context.scheduled} scheduled
      </span>
      <span className="text-gray-600">·</span>
      <span className="flex items-center gap-1 text-gray-400">
        <CheckCircle size={11} /> {context.pending} pending
      </span>
      {context.overloaded && (
        <>
          <span className="text-gray-600">·</span>
          <span className="flex items-center gap-1 text-amber-400">
            <AlertTriangle size={11} /> overloaded
          </span>
        </>
      )}
    </div>
  );
};

const AskSpaceCal = () => {
  const { auth } = useContext(AuthContext);
  const { bumpTasks } = useContext(TasksContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Ask SpaceCal — your personal time assistant. I can see your real schedule and help you prevent overload, plan your week, or just think through your time. What's on your mind?",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const sendMessage = async (text, baseMessages) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    setInput('');
    setShowSuggestions(false);
    const newMessages = [...(baseMessages || messages), { role: 'user', content: userText }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const history = newMessages.slice(0, -1).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await axios.post(
        `${API}/assistant/ask`,
        { message: userText, conversation_history: history, tz_offset_minutes: -new Date().getTimezoneOffset() },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.reply, action: response.data.action }]);
      if (response.data.context) setContext(response.data.context);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "Sorry, I couldn't reach the server right now. Try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const applyTrip = async (trip, decisions) => {
    try {
      const res = await axios.post(`${API}/trips/apply`, {
        destination: trip.destination, start_date: trip.start_date, end_date: trip.end_date,
        tz_offset_minutes: -new Date().getTimezoneOffset(), decisions,
      }, { headers: { Authorization: `Bearer ${auth.token}` } });
      const r = res.data.results || [];
      const count = (a) => r.filter(x => x.action === a).length;
      const summary = `Done ✅ ${trip.label} is blocked. Rescheduled: ${count('rescheduled')}, paused: ${count('paused') + count('paused_for_trip')}` +
        (count('removed') ? `, removed: ${count('removed')}` : '') + '.';
      toast.success('Trip saved and calendar updated');
      bumpTasks();
      const base = [...messages.map(m => (m.action?.type === 'trip_review' && m.action.trip.start_date === trip.start_date ? { ...m, action: { ...m.action, applied: true } } : m)),
        { role: 'assistant', content: summary }];
      setMessages(base);
      const where = trip.destination ? ` to ${trip.destination}` : '';
      setTimeout(() => sendMessage(`Help me plan my trip${where} from ${trip.start_date} to ${trip.end_date}`, base), 300);
      return true;
    } catch {
      toast.error('Could not apply the trip changes');
      return false;
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="ask-spacecal-toggle-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
        style={{
          background: 'linear-gradient(135deg, #00D9FF 0%, #0EA5E9 100%)',
          boxShadow: '0 4px 32px rgba(0,217,255,0.45)',
        }}
        aria-label="Ask SpaceCal"
      >
        {open
          ? <ChevronDown size={24} color="#0A0E27" />
          : <Sparkles size={22} color="#0A0E27" />
        }
      </button>

      {/* Chat panel */}
      <div
        className={`fixed bottom-24 right-6 z-50 w-[360px] rounded-2xl overflow-hidden flex flex-col transition-all duration-300 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{
          height: '520px',
          maxWidth: 'calc(100vw - 2rem)',
          maxHeight: 'calc(100vh - 8rem)',
          background: 'rgba(10,14,39,0.97)',
          border: '1px solid rgba(0,217,255,0.18)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,217,255,0.08)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8"
          style={{ background: 'rgba(0,217,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #00D9FF, #0EA5E9)' }}>
              <Bot size={16} color="#0A0E27" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-none">Ask SpaceCal</p>
              <p className="text-[10px] text-teal-400 leading-none mt-0.5">Your time assistant</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="chat-close-btn"
            className="text-gray-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5"
          >
            <X size={16} />
          </button>
        </div>

        {/* Context bar */}
        <ContextBadge context={context} />

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5"
                  style={{ background: 'linear-gradient(135deg, #00D9FF, #0EA5E9)' }}>
                  <Bot size={11} color="#0A0E27" />
                </div>
              )}
              <div
                className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'text-white rounded-tr-sm'
                    : 'text-gray-100 rounded-tl-sm'
                }`}
                style={
                  msg.role === 'user'
                    ? { background: 'linear-gradient(135deg, #00D9FF22, #0EA5E922)', border: '1px solid rgba(0,217,255,0.25)' }
                    : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }
                }
              >
                <div className="whitespace-pre-line" data-testid={`chat-message-${i}`}>{msg.content}</div>
                {msg.action?.type === 'trip_review' && (
                  <TripReviewCard action={msg.action} onApply={applyTrip} />
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-2"
                style={{ background: 'linear-gradient(135deg, #00D9FF, #0EA5E9)' }}>
                <Bot size={11} color="#0A0E27" />
              </div>
              <div className="rounded-2xl rounded-tl-sm px-4 py-3"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Loader2 size={14} className="text-teal-400 animate-spin" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        {showSuggestions && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                className="text-[11px] px-2.5 py-1 rounded-full text-teal-300 transition-colors hover:text-white"
                style={{ background: 'rgba(0,217,255,0.08)', border: '1px solid rgba(0,217,255,0.2)' }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-3 pb-3 pt-2 border-t border-white/5">
          <div className="flex items-center gap-2 rounded-xl px-3 py-2"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about your schedule…"
              data-testid="chat-input"
              disabled={loading}
              className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none"
            />
            <button
              onClick={() => sendMessage()}
              data-testid="chat-send-btn"
              disabled={!input.trim() || loading}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-30 hover:scale-110 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #00D9FF, #0EA5E9)' }}
            >
              <Send size={13} color="#0A0E27" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AskSpaceCal;
